import { useMemo } from 'react';
import {
  ChevronDoubleRightIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useGroupedFilters, Tooltip } from '@plerionhq/design-system';
import type { Filter } from '@plerionhq/design-system';
import { useSearchParams } from 'react-router-dom';
import { useListResourceTypes } from '../../api/use-list-resource-types';
import type { TFunction } from 'i18next';
import {
  mechanismDescriptions,
  mechanismLabels,
  originDescriptions,
  originLabels,
  REVIEW_DECISIONS,
  reviewDecisionLabels,
  scopeDescriptions,
  scopeLabels,
  trustDescriptions,
  trustLabels,
} from '../../label-maps';

import { useTranslation } from 'react-i18next';

function withTooltip(displayLabel: string, description: string) {
  return {
    label: (
      <span className="flex w-full min-w-0 items-center justify-between gap-1">
        <span className="truncate">{displayLabel}</span>
        <Tooltip label={description}>
          <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
        </Tooltip>
      </span>
    ),
    searchString: displayLabel,
  };
}

// The two review-date bounds, as plain date inputs. The design-system
// `dateRange` facet is built for past ranges — it offers "Last 7 days" and
// calls its custom option "a range in the past" — and a next review date is
// normally in the future, so almost none of it applies here. These also let
// either bound stand alone, which `dateRange` does not: it renders a value only
// when both ends are set, so the Past due chip could never fill in just the
// upper bound.
const nextReviewBounds = (t: TFunction<'accessGrants'>) =>
  [
    { key: 'nextReviewFrom', label: t('from') },
    { key: 'nextReviewAtEnd', label: t('to') },
  ] as const;

const dateFieldClass =
  'w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-action-primary focus:ring-1 focus:ring-action-primary focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

function NextReviewRangeFilter() {
  const { t } = useTranslation('accessGrants');
  const [searchParams, setSearchParams] = useSearchParams();

  const setBound = (key: string, value: string) => {
    // Functional update: the grouped-filter hook writes to the same query
    // string, so building from a captured snapshot can drop its edits.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  return (
    <div className="w-full">
      <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {t('nextReview')}
      </span>
      <div className="grid grid-cols-2 gap-2">
        {nextReviewBounds(t).map(({ key, label }) => (
          <label key={key} className="block">
            <span className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
              {label}
            </span>
            <input
              type="date"
              className={dateFieldClass}
              // slice so a link carrying a full ISO timestamp still populates
              // the input, which only accepts YYYY-MM-DD.
              value={(searchParams.get(key) ?? '').slice(0, 10)}
              onChange={(e) => setBound(key, e.target.value)}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

type AccessGrantsFiltersProperties = {
  toggleExpandFilter?: () => void;
};

export function AccessGrantsFilters({
  toggleExpandFilter,
}: AccessGrantsFiltersProperties) {
  const { t } = useTranslation('accessGrants');
  const {
    data: resourceTypeData,
    error: resourceTypeError,
    isLoading: resourceTypeLoading,
  } = useListResourceTypes();

  const filters: Filter[] = useMemo(
    () => [
      {
        type: 'search',
        id: 'search',
        label: t('common:search'),
        permanent: true,
        justifyCenter: false,
        showClear: true,
      },
      // Order below mirrors the table's default column order:
      // Asset name · Principal · Mechanism · Scope · Origin · Trust ·
      // Resource type · Principal type · Asset account · Principal account,
      // then the review-lifecycle facets that only the Access review view
      // shows as columns.
      {
        type: 'search',
        id: 'assetName',
        label: t('assetName'),
        permanent: true,
        showClear: true,
      },
      {
        type: 'search',
        id: 'principal',
        label: t('principal'),
        permanent: true,
        showClear: true,
      },
      {
        type: 'multiSelect',
        id: 'mechanism',
        label: t('mechanism'),
        permanent: true,
        displayMaxItems: 2,
        options: [
          {
            value: 'resource_policy',
            ...withTooltip(
              mechanismLabels(t).resource_policy,
              mechanismDescriptions(t).resource_policy,
            ),
          },
          {
            value: 'trust_policy',
            ...withTooltip(
              mechanismLabels(t).trust_policy,
              mechanismDescriptions(t).trust_policy,
            ),
          },
          {
            value: 'ram_share',
            ...withTooltip(
              mechanismLabels(t).ram_share,
              mechanismDescriptions(t).ram_share,
            ),
          },
          {
            value: 'attribute_share',
            ...withTooltip(
              mechanismLabels(t).attribute_share,
              mechanismDescriptions(t).attribute_share,
            ),
          },
        ],
        showInlineClear: true,
        hideIcon: true,
      },
      {
        type: 'multiSelect',
        id: 'grantScope',
        label: t('scope'),
        permanent: true,
        displayMaxItems: 2,
        options: [
          {
            value: 'public',
            ...withTooltip(scopeLabels(t).public, scopeDescriptions(t).public),
          },
          {
            value: 'cross-org',
            ...withTooltip(
              scopeLabels(t)['cross-org'],
              scopeDescriptions(t)['cross-org'],
            ),
          },
          {
            value: 'federated',
            ...withTooltip(
              scopeLabels(t).federated,
              scopeDescriptions(t).federated,
            ),
          },
          {
            value: 'same-org',
            ...withTooltip(
              scopeLabels(t)['same-org'],
              scopeDescriptions(t)['same-org'],
            ),
          },
          {
            value: 'same-account',
            ...withTooltip(
              scopeLabels(t)['same-account'],
              scopeDescriptions(t)['same-account'],
            ),
          },
          {
            value: 'aws-service',
            ...withTooltip(
              scopeLabels(t)['aws-service'],
              scopeDescriptions(t)['aws-service'],
            ),
          },
        ],
        showInlineClear: true,
        hideIcon: true,
      },
      {
        type: 'multiSelect',
        id: 'grantOrigin',
        label: t('origin'),
        permanent: true,
        displayMaxItems: 2,
        options: [
          {
            value: 'ext',
            ...withTooltip(originLabels(t).ext, originDescriptions(t).ext),
          },
          {
            value: 'int',
            ...withTooltip(originLabels(t).int, originDescriptions(t).int),
          },
        ],
        showInlineClear: true,
        hideIcon: true,
      },
      {
        type: 'multiSelect',
        id: 'trustStatus',
        label: t('common:trust'),
        permanent: true,
        displayMaxItems: 2,
        options: [
          {
            value: 'trusted',
            ...withTooltip(
              trustLabels(t).trusted,
              trustDescriptions(t).trusted,
            ),
          },
          {
            value: 'untrusted',
            ...withTooltip(
              trustLabels(t).untrusted,
              trustDescriptions(t).untrusted,
            ),
          },
        ],
        showInlineClear: true,
        hideIcon: true,
      },
      {
        id: 'assetType',
        label: t('resourceType'),
        type: 'resourceType',
        data: resourceTypeData ?? [],
        error: resourceTypeError ?? undefined,
        loading: resourceTypeLoading,
        permanent: true,
        displayMaxItems: 1,
        selectText: t('resourceType'),
        showInlineClear: true,
        hideIcon: true,
      },
      {
        type: 'multiSelect',
        id: 'principalType',
        label: t('principalType'),
        permanent: true,
        displayMaxItems: 2,
        options: [
          { value: 'aws_account', label: t('awsAccount') },
          { value: 'aws_role', label: t('awsRole') },
          { value: 'aws_user', label: t('awsUser') },
          { value: 'public', label: t('any') },
          { value: 'conditional', label: t('anyConditional') },
          { value: 'oidc', label: 'OIDC' },
          { value: 'saml', label: 'SAML' },
          { value: 'aws_service', label: t('awsService') },
          { value: 'cognito_identity', label: t('cognitoIdentity') },
          { value: 'external_idp', label: t('externalIdP') },
          { value: 'canonical_user', label: t('canonicalUser') },
          { value: 'aws_org', label: t('awsOrganization') },
          { value: 'aws_support', label: t('awsSupport') },
        ].sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
        ),
        showInlineClear: true,
        hideIcon: true,
      },
      // Asset / Principal account inputs accept comma-separated 12-digit
      // AWS account ids. Backend ANDs both filters; multiple values OR
      // inside each filter (Op.in). Whitespace around commas is trimmed
      // server-side in parseArray.
      {
        type: 'search',
        id: 'resourceAccountId',
        label: t('assetAccount'),
        placeholder: '123456789012, 987654321098',
        permanent: true,
        showClear: true,
      },
      {
        type: 'search',
        id: 'principalAccountId',
        label: t('principalAccount'),
        placeholder: '123456789012, 987654321098',
        permanent: true,
        showClear: true,
      },
      // Review-lifecycle facets. These match the editable columns in the
      // Access review view, which otherwise had no way to be sliced — the
      // filters above all describe columns that view hides.
      {
        type: 'multiSelect',
        id: 'reviewDecision',
        label: t('decision'),
        permanent: true,
        displayMaxItems: 2,
        options: REVIEW_DECISIONS.map((decision) => ({
          value: decision,
          label: reviewDecisionLabels(t)[decision],
        })),
        showInlineClear: true,
        hideIcon: true,
      },
      {
        // `grantee` is the parameter the endpoint reads; it rejects unknown
        // query params outright, so the id doubles as the wire name.
        type: 'search',
        id: 'grantee',
        label: t('grantee'),
        permanent: true,
        showClear: true,
      },
    ],
    [resourceTypeData, resourceTypeError, resourceTypeLoading, t],
  );

  const { filtersRender, visibleFilters, setFilterValue } = useGroupedFilters({
    filters,
    groups: [],
    mode: 'query',
  });

  const clearAll = () => {
    // The review-date bounds are not part of the grouped-filter set, so they are cleared by key
    // or Clear all would leave them behind. Every write goes through setFilterValue, which builds
    // on the previous one and skips writes that change nothing. The first write that changes the
    // URL adds a history entry and the rest replace it, so Clear all is one Back step.
    const keys = [
      ...filters.map((filter) => ({ id: filter.id, filter })),
      ...nextReviewBounds(t).map(({ key }) => ({ id: key, filter: undefined })),
    ];
    const before = window.location.search;
    for (const { id, filter } of keys) {
      setFilterValue(id, '', filter, {
        replace: window.location.search !== before,
      });
    }
  };

  return (
    <div className="mb-2 flex w-full flex-col overflow-y-auto">
      <div className="flex w-full flex-col gap-2">
        <div className="flex justify-between gap-1 p-3">
          {toggleExpandFilter && (
            <button
              type="button"
              className="rounded-md p-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600"
              onClick={toggleExpandFilter}
            >
              <ChevronDoubleRightIcon className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            className="rounded-md p-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600"
            onClick={clearAll}
          >
            {t('clearAll')}
          </button>
        </div>
        <div className="flex flex-col items-start gap-2 p-3 pt-0">
          {visibleFilters.length > 0 && (
            <div className="flex w-full flex-col gap-2">
              {filtersRender.generic}
            </div>
          )}
          <NextReviewRangeFilter />
        </div>
      </div>
    </div>
  );
}
